/**
 * Root view model of the wallet page: the connection to the node, the session state the views share,
 * the current view and the view models. Bound once to #wallet-app by app.ts.
 *
 * Page templates: connecting-template while the configuration and the node status load,
 * node-error-template when the read API did not answer (the wallet is not usable without it),
 * wallet-template with the navigation, the status panel and the current view.
 */

import ko from 'knockout';
import { configureApi, configureReadApi, fetchStatus, fetchAccount, fetchTransactions } from 'streembit-bc-sdk/api';
import type { ChainStatus, AccountInfo, TransactionRecord } from 'streembit-bc-sdk/api';
import { ModelBase } from './base';
import { OpenModel } from './open';
import { CreateModel } from './create';
import { SendModel } from './send';
import { ReceiveModel } from './receive';
import { ContractsModel } from './contracts';
import type { Session } from '../session';
import { currentSession, lockSession } from '../session';
import { loadConfig } from '../platform';
import { loadStoredWallet, requestPersistence } from '../store';
import type { StoredWallet } from '../store';
import { FEE } from '../transactions';

/** The four tabs. `open` is the Wallet tab; creating or restoring a wallet runs inside it. */
export type ViewName = 'open' | 'send' | 'receive' | 'contracts';

/** Lock after this long without pointer or keyboard activity. */
const IDLE_LOCK_MS = 15 * 60 * 1000;
/** While the tab is visible: ask the node for its status this often, and read the account only when the height moved. */
const POLL_MS = 5 * 1000;
/** After the wallet's own submission: read the account this often until its sequence moves, at most for BURST_MAX_MS. */
const BURST_MS = 2 * 1000;
const BURST_MAX_MS = 30 * 1000;

export class WalletModel extends ModelBase {
    readonly fee = FEE;

    // node and read API: status, balances and history come from the read API, submission goes to the node
    readonly apiBaseUrl = ko.observable('');
    readonly readApiBaseUrl = ko.observable('');
    readonly chain = ko.observable<ChainStatus | null>(null);
    readonly nodeError = ko.observable('');
    /** The address that did not answer, for the error page; empty when the configuration itself failed. */
    readonly failedUrl = ko.observable('');

    // the wallet kept in this browser and the session
    readonly stored = ko.observable<StoredWallet | null>(null);
    readonly persisted = ko.observable(true);
    readonly unlocked = ko.observable(false);
    readonly address = ko.observable('');
    readonly publicKey = ko.observable('');
    readonly derivationPath = ko.observable('');
    readonly account = ko.observable<AccountInfo | null>(null);
    readonly transactions = ko.observableArray<TransactionRecord>([]);
    /** Information for the user that is not an error, shown above the view. */
    readonly notice = ko.observable('');
    /** When the node was last asked, for the status panel. */
    readonly updatedAt = ko.observable<Date | null>(null);
    readonly updatedText = ko.pureComputed(() => {
        const at = this.updatedAt();
        return at ? `Updated ${at.toTimeString().slice(0, 8)}` : '';
    });

    // status panel
    readonly stateText = ko.pureComputed(() => this.unlocked() ? 'Unlocked' : this.stored() ? 'Locked' : 'No wallet in this browser');
    readonly networkText = ko.pureComputed(() => this.chain()?.network ?? '—');
    readonly nodeText = ko.pureComputed(() => {
        const c = this.chain();
        return c ? `Connected, height ${c.height}` : 'Connecting';
    });
    readonly chainText = ko.pureComputed(() => {
        const c = this.chain();
        return c ? `${c.network}, chain ${c.chainId}` : 'unknown';
    });
    readonly storageWarning = ko.pureComputed(() => !this.persisted() && Boolean(this.stored()));

    // views
    readonly views: ReadonlyArray<{ key: ViewName; label: string }> = [
        { key: 'open', label: 'Wallet' },
        { key: 'send', label: 'Send' },
        { key: 'receive', label: 'Receive' },
        { key: 'contracts', label: 'Contracts' }
    ];
    readonly view = ko.observable<ViewName>('open');
    readonly viewTemplate = ko.pureComputed(() => `${this.view()}-view-template`);
    readonly open = new OpenModel(this);
    readonly send = new SendModel(this);
    readonly receive = new ReceiveModel(this);
    readonly contracts = new ContractsModel(this);
    /** The create or restore flow, hosted by the Wallet tab. */
    readonly create = new CreateModel(this);
    readonly viewModel = ko.pureComputed<ModelBase>(() => this[this.view()]);

    private idleTimer = 0;
    private pollTimer = 0;
    private burstTimer = 0;
    private burstEnd = 0;
    private burstFromSequence = -1;

    constructor() {
        super();
        this.template('connecting-template');
    }

    /** Loads the configuration and the stored wallet, then connects to the node. */
    async init(): Promise<void> {
        try {
            this.stored(await loadStoredWallet());
        } catch (err) {
            this.notice(`The wallet kept in this browser could not be read: ${messageOf(err)}`);
        }
        try {
            const config = await loadConfig();
            this.apiBaseUrl(config.apiBaseUrl);
            this.readApiBaseUrl(config.readApiBaseUrl);
            configureApi(config.apiBaseUrl);
            configureReadApi(config.readApiBaseUrl);
            // The server's last reading of the node, for the first paint; the node's own answer follows.
            if (config.status) this.chain(config.status);
        } catch (err) {
            this.showNodeError(`The wallet configuration could not be read: ${messageOf(err)}`, '');
            return;
        }
        await this.connect();
    }

    /** Asks the read API for the status; the wallet shows its views only when it answers. */
    async connect(): Promise<void> {
        try {
            this.chain(await fetchStatus());
            this.updatedAt(new Date());
            this.nodeError('');
            this.template('wallet-template');
        } catch (err) {
            this.showNodeError(messageOf(err), this.readApiBaseUrl());
            return;
        }
        await this.contracts.refreshCatalogue();
        await this.refreshAccount();
        this.startPolling();
    }

    retry(): void {
        this.template('connecting-template');
        void this.connect();
    }

    private showNodeError(reason: string, url: string): void {
        this.stopPolling();
        this.chain(null);
        this.nodeError(reason);
        this.failedUrl(url);
        this.template('node-error-template');
    }

    // ---- keeping the page current without a connection held open on the node ----

    /** Called by the bootstrap when the tab is shown or hidden: a hidden tab asks the node nothing. */
    visibilityChanged(): void {
        if (document.visibilityState === 'visible' && this.template() === 'wallet-template') {
            void this.poll();
            this.startPolling();
        } else {
            this.stopPolling();
        }
    }

    private startPolling(): void {
        this.stopPolling();
        if (document.visibilityState !== 'visible') return;
        this.pollTimer = window.setInterval(() => void this.poll(), POLL_MS);
    }

    private stopPolling(): void {
        clearInterval(this.pollTimer);
        this.pollTimer = 0;
        clearInterval(this.burstTimer);
        this.burstTimer = 0;
    }

    /** One status read; the account follows only when the height moved. A read API that stops answering shows the error page. */
    private async poll(): Promise<void> {
        let status: ChainStatus;
        try {
            status = await fetchStatus();
        } catch (err) {
            this.showNodeError(messageOf(err), this.readApiBaseUrl());
            return;
        }
        this.updatedAt(new Date());
        const moved = status.height !== this.chain()?.height;
        this.chain(status);
        if (moved) await this.refreshAccount();
    }

    /** After the wallet's own submission: read the account every few seconds until its sequence moves. */
    expectAccountChange(): void {
        this.burstFromSequence = this.account()?.sequence ?? -1;
        this.burstEnd = Date.now() + BURST_MAX_MS;
        clearInterval(this.burstTimer);
        this.burstTimer = window.setInterval(() => void this.burst(), BURST_MS);
        void this.burst();
    }

    private async burst(): Promise<void> {
        if (!this.unlocked() || Date.now() > this.burstEnd) {
            clearInterval(this.burstTimer);
            this.burstTimer = 0;
            return;
        }
        await this.refreshAccount();
        if ((this.account()?.sequence ?? -1) !== this.burstFromSequence) {
            clearInterval(this.burstTimer);
            this.burstTimer = 0;
        }
    }

    navigate(view: ViewName): void {
        if (!this.views.some(v => v.key === view)) return;
        // leaving a tab closes its result panel and its review, so it opens on the form next time
        if (view !== this.view()) {
            if (this.view() === 'send') this.send.leave();
            if (this.view() === 'contracts') this.contracts.leave();
        }
        this.notice('');
        this.view(view);
        window.scrollTo({ top: 0 });
    }

    /** Status, account and catalogue again, on the Refresh button. */
    async refresh(): Promise<void> {
        await this.connect();
    }

    /** Reads the account; the history follows only when the balance or the sequence changed. */
    async refreshAccount(): Promise<void> {
        const session = currentSession();
        if (!session) return;
        const address = session.account.address;
        const before = this.account();
        let account: AccountInfo;
        try {
            account = await fetchAccount(address);
        } catch (err) {
            this.account(null);
            this.open.displayErrors(`Balance unavailable: ${messageOf(err)}`);
            return;
        }
        this.account(account);
        this.updatedAt(new Date());
        if (before && before.balance === account.balance && before.sequence === account.sequence) return;
        try {
            this.transactions(await fetchTransactions(address));
        } catch {
            this.transactions([]);
        }
    }

    async reloadStored(): Promise<void> {
        this.stored(await loadStoredWallet());
        this.persisted(this.stored() ? await requestPersistence() : true);
    }

    /** Called by the views once the session module holds an unlocked wallet. */
    startSession(session: Session): void {
        this.address(session.account.address);
        this.publicKey(session.account.publicKey);
        this.derivationPath(session.account.path);
        this.unlocked(true);
        this.account(null);
        this.transactions([]);
        this.touch();
        void this.refreshAccount();
    }

    lock(): void {
        lockSession();
        clearTimeout(this.idleTimer);
        this.unlocked(false);
        this.address('');
        this.publicKey('');
        this.derivationPath('');
        this.account(null);
        this.transactions([]);
        this.send.leave();
        this.contracts.leave();
    }

    /** Pointer or keyboard activity: restarts the idle lock while the wallet is unlocked. */
    touch(): void {
        clearTimeout(this.idleTimer);
        if (!this.unlocked()) return;
        this.idleTimer = window.setTimeout(() => {
            this.lock();
            this.notice('The wallet was locked after 15 minutes without activity.');
        }, IDLE_LOCK_MS);
    }
}

function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}
